import React, { memo, useEffect, useRef, useState } from 'react';
import { ChatFooterFileIcon, ChatMessageActionIcon, ChatMessageSentIcon, DoubleCheckIcon, SingleCheckIcon } from "../Public/markup/icons";
import { getUrl } from '../../api';
import { PhotoPopup } from "../Viewer/PhotoSlider";
import MiniLoader from '../Loaders/MiniLoader';
import CopyToClipboard from 'react-copy-to-clipboard';
import AppPopover from '../core/Popover';
import classNames from 'classnames';
import { useChatContext } from './ChatContextApi';
import { filterAndMap } from '../../utils';
import Avatar from 'react-avatar';

const getFileExtension = (fileName = "") => fileName.split('.').pop().toLowerCase();

const parseMessageContent = (message) => { // "Текст [i]курсив[/i], [b]жирный[/b] и [u]подчеркнутый[/u]"

  const TAG_PATTERN = /(?:\[(i|b|u)\])(.*?)(?:\[(\/\1)\])|([^[]+)/g;

  const getFormattedTag = (match) => match[1] || null;

  return Array.from(
    message.matchAll(TAG_PATTERN),
    (match) => ({
      text: match[2] ?? match[4],
      formatted: getFormattedTag(match),
    })
  );
};

const parsedToHtml = (messageArray) => {
  const TAG_MAP = {
    i: { open: '<span class="italic" >', close: '</span>' },
    b: { open: '<span class="bold">', close: '</span>' },
    u: { open: '<span class="underline">', close: '</span>' },
  };

  const wrapText = ({ text, formatted }) =>
    formatted && TAG_MAP[formatted]
      ? `${TAG_MAP[formatted].open}${text}${TAG_MAP[formatted].close}`
      : text;

  return messageArray.map(wrapText).join('');

};

const contentToHtml = content => {
  if (!content) return null;
  const parsedContent = parseMessageContent(content);
  const htmlContent = parsedToHtml(parsedContent);
  return htmlContent;
}

const fetchMediaBlob = async url => {
  try {
    const response = await fetch(url, {
      method: 'GET',
      mode: "cors",
      credentials: "include",
    });
    if (!response.ok) throw new Error(`HTTP error: ${response.status}`);

    const blob = await response.blob();
    const objectURL = URL.createObjectURL(blob);

    return {
      url: objectURL,
      type: blob.type,
      error: null,
    };
  } catch (error) {
    console.error('Не удалось получить файл :>>', error, url);
    return {
      url: null,
      type: null,
      error: error.message,
    };
  }
};

const extensionToMediaType = {
  // Изображения
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  avif: 'image/avif',
  bmp: 'image/bmp',
  ico: 'image/x-icon',
  svg: 'image/svg+xml',

  // Видео
  mp4: 'video/mp4',
  webm: 'video/webm',
  ogv: 'video/ogg',
  ogg: 'video/ogg', // Для .ogg, используемых как видео
  mov: 'video/quicktime',
  mkv: 'video/x-matroska',
  '3gp': 'video/3gpp',
  '3gpp': 'video/3gpp',

  // Аудио
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  oga: 'audio/ogg',
  vorbis: 'audio/ogg', // Для .ogg с Vorbis
  opus: 'audio/ogg', // Для .ogg с Opus
  aac: 'audio/aac',
  m4a: 'audio/mp4',
  flac: 'audio/flac',
  weba: 'audio/webm',
};

export const MessageFile = memo(({ file, openGallery, files, index }) => {
  const [fileState, setfileState] = useState({})

  useEffect(() => {
    fetchMediaBlob(file.DOWNLOAD_URL || file.urlDownload)
      .then(setfileState)

  }, [])

  const fileExtension = getFileExtension(file.NAME || file.name);
  const mediaType = extensionToMediaType[fileExtension]

  if (!fileState?.url || !mediaType) return <a
    key={file.ID || file.id}
    href={file.DOWNLOAD_URL || file.urlDownload}
    target="_blank"
    rel="noopener noreferrer"
    className="open-chat__message-file-link"
  >
    {file.NAME || file.name}
  </a>

  if (mediaType.includes("audio")) return <audio
    key={file.ID || file.id}
    controls
    src={fileState?.url}
    name={file.NAME || file.name}
  />

  if (mediaType.includes("image")) return <img
    key={file.ID || file.id}
    src={file.DOWNLOAD_URL || file.urlDownload}
    alt={file.NAME || file.name}
    className="open-chat__message-file-image"
    onClick={() => openGallery(filterAndMap(files, (item) => item.type === "image" && { ...item.image, src: getUrl(item.DOWNLOAD_URL) }), index + 1)}
  />

  if (mediaType.includes("video")) return <video
    key={file.ID || file.id}
    controls
    className="open-chat__message-file-video"
    src={file.DOWNLOAD_URL || file.urlDownload}
  >
    Ваш браузер не поддерживает видео.
  </video>

  return <a
    key={file.ID || file.id}
    href={file.DOWNLOAD_URL || file.urlDownload}
    target="_blank"
    rel="noopener noreferrer"
    className="open-chat__message-file-link"
  >
    {file.NAME || file.name}
  </a>
})

const MessageFiles = ({ files, openGallery }) => {

  return files.map((file, index) => <MessageFile file={file} files={files} index={index} openGallery={openGallery} key={file.ID || file.id} />)
};

const Message = memo(props => {
  const {
    fromMe,
    time,
    images = [],
    file,
    content,
    content_type,
    unread,
    viewed,
    viewedByOthers,
    id,
    observer,
    deleteMsg,
    forwardMessage,
    replyMessage,
    error,
    scrollToMessage,
    status,
    onEditClick,
    files,
    forward,
    isSavedMessage,
    saveMessage,
    systemMessage,
    author,
    params
  } = props;

  const messageRef = useRef();

  const { openGallery, setGalleryIndex, setValues, members } = useChatContext();

  const { ATTACH } = params || {};

  useEffect(() => {
    if (viewed || !messageRef.current || !observer) return;
    observer.observe(messageRef.current);
  }, [viewed, messageRef, observer]);

  const onClickToMessage = (e) => {
    const replyElement = e.target.closest(".reply");
    if (replyElement && replyElement.getAttribute("message-id")) {
      scrollToMessage({ id: `message-${replyElement.getAttribute("message-id")}` });
    }
  };

  return (
    <>
      {systemMessage && <div className="open-chat__date" style={{ textAlign: "center" }}>
        {systemMessage.text}
        {systemMessage.users.map(({ id, name }) => <div
          onClick={() => setValues(prevState => ({ ...prevState, currentChat: members.find(m => m.id == id) ? { ...members.find(m => m.id == id), requestID: `members-${id}` } : { id, title: name, chatType: "private", user: { id, name }, requestID: `members-${id}` }, chatSearchResult: false, scrollMessage: null, reply: null }))}
          style={{
            fontWeight: "bold",
            color: "#443f3f",
            textDecoration: "underline"
          }}
          id={`message-${id}`}
          ref={messageRef}
        >
          {name}
        </div>)}
      </div>}

      {!systemMessage && <div
        className={!fromMe ? "open-chat__overmessage" : "open-chat__overmessage open-chat__overmessage_me"}
        id={`message-${id}`}
        ref={messageRef}
        onClick={onClickToMessage}
      >

        {!fromMe && (
          author?.avatar ? <img
            src={author?.avatar}
            className="chat-item__logo"
            style={{
              marginRight: 10,
              marginTop: 10,
              width: 35,
              height: 35,
              borderRadius: "50%",
            }}
          /> :
            <Avatar
              name={author?.name}
              size="35"
              round={true}
              textSizeRatio={2.857}
              style={{
                marginRight: 10,
                marginTop: 10,
              }}
            />)
        }

        <div>
          {author?.name && !fromMe && <div style={{
            fontSize: 12,
            color: "gray"
          }}>
            {author.name}{author.workPosition ? `, ${author.workPosition}` : ""}
          </div>}

          <div className={classNames({
            "open-chat__message": true,
            "open-chat__message_me": fromMe,
            "open-chat__message-image": images.length,
            "open-chat__message_viewed": viewed,
            "open-chat__message_file": file,
            "open-chat__message_error": error,
          })}
          >
            {forward && <div className="italic">
              {forward.author ? `Переслано от ${forward.author.title}` : "Пересланное сообщение:"}
            </div>}

            {ATTACH && <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: content || files ? 5 : 0 }}>
              {ATTACH.map((item, index) => {
                return (
                  <div className="attach" key={index} style={{ borderColor: item.COLOR }}>
                    <div className="attach__item-text">{item.DESCRIPTION}</div>
                  </div>
                )
              })}
            </div>}

            {files && <MessageFiles files={files || []} openGallery={openGallery} />}

            {content && (<div
              className={
                classNames({
                  "open-chat__message-text": true,
                  "open-chat__message-text_me": fromMe,
                  "attach": forward,
                  "open-chat__message-forwarded-text": forward,
                })}
              dangerouslySetInnerHTML={{ __html: contentToHtml(content) }}
            />)}

            {!!error && <p> Ошибка: {error}</p>}
            <div className="open-chat__message-info">
              <div className="open-chat__message-time">{time}</div>
              {status === "sending" && <div className="open-chat__message-status"><MiniLoader /></div>}
              {!isSavedMessage && !viewedByOthers && status !== "sending" && fromMe && <div className="open-chat__message-status"><SingleCheckIcon /></div>}
              {((viewedByOthers && fromMe) || isSavedMessage) && (
                <div className="open-chat__message-status">
                  <DoubleCheckIcon />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="open-chat__message-actions">
          <span className="open-chat__message-actionbtn">
            <ChatMessageActionIcon />
            <ul className="open-chat__action-list">
              {!fromMe && <li onClick={replyMessage}>Ответить</li>}
              {!isSavedMessage && <li onClick={saveMessage}>Сохранить</li>}
              <li onClick={forwardMessage}>Переслать</li>
              <AppPopover trigger="click" content="Текст сообщения скопирован" overlayClassName={'share-popover'} >
                <CopyToClipboard text={content}>
                  <li>Копировать</li>
                </CopyToClipboard>
              </AppPopover>
              {fromMe && <li onClick={onEditClick}>Редактировать</li>}
              {fromMe && <li style={{ color: "#FF2D23FF" }} onClick={deleteMsg}>
                Удалить
              </li>}
            </ul>
          </span>
        </div>
      </div>}

    </>

  );
});

export default Message;